import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { IconCalendar, IconChart, IconCheck, IconClients, IconDownload, IconHanko, IconPayment, IconWarning, IconWorkProof } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { isDemoMode, redirectToSignup } from '../lib/demoMode';
import { DEMO_USER, DEMO_PROFILE, DEMO_PROJECTS, DEMO_WORK_DATES, DEMO_CLIENTS } from '../lib/demoData';
import { usePlan } from '../lib/PlanContext';

const formatDateJP = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日`;
};
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
};
const getDayName  = (dateStr) => ['日','月','火','水','木','金','土'][new Date(dateStr).getDay()];
const getDayIndex = (dateStr) => new Date(dateStr).getDay();

// ── 電子印SVG生成（苗字から自動生成）─────────────────────────
// ── 電子印SVG生成（プロフィールの入力文字から生成・1〜4文字対応）──
function buildHankoSVG(text, size = 56) {
  const chars = (text || '').slice(0, 4);
  if (!chars) return null;
  const r = size / 2;
  const len = chars.length;
  const fontSize = len === 1 ? size * 0.42
                 : len === 2 ? size * 0.36
                 : len === 3 ? size * 0.28
                 : size * 0.24;
  const getTextNodes = () => {
    if (len === 1) {
      return `<text x="${r}" y="${r + fontSize*0.38}" text-anchor="middle" dominant-baseline="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars}</text>`;
    }
    if (len === 2) {
      const y1 = r * 0.72 + fontSize * 0.35;
      const y2 = r * 1.42 + fontSize * 0.35;
      return `<text x="${r}" y="${y1}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[0]}</text>
       <text x="${r}" y="${y2}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[1]}</text>`;
    }
    if (len === 3) {
      const cx1 = r * 0.58, cx2 = r * 1.42;
      const yTop = r * 0.62 + fontSize*0.35, yBot = r * 1.38 + fontSize*0.35, yMid = r + fontSize*0.38;
      return `<text x="${cx1}" y="${yTop}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[0]}</text>
       <text x="${cx1}" y="${yBot}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[1]}</text>
       <text x="${cx2}" y="${yMid}" text-anchor="middle" dominant-baseline="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[2]}</text>`;
    }
    const cx1 = r * 0.58, cx2 = r * 1.42;
    const yTop = r * 0.62 + fontSize*0.35, yBot = r * 1.38 + fontSize*0.35;
    return `<text x="${cx1}" y="${yTop}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[0]}</text>
       <text x="${cx1}" y="${yBot}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[1]}</text>
       <text x="${cx2}" y="${yTop}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[2]}</text>
       <text x="${cx2}" y="${yBot}" text-anchor="middle"
        font-family="'Noto Serif JP','Hiragino Mincho Pro','Yu Mincho','serif'"
        font-size="${fontSize}" font-weight="700" fill="#c0392b">${chars[3]}</text>`;
  };
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <circle cx="${r}" cy="${r}" r="${r-2}" fill="none" stroke="#c0392b" stroke-width="2.5"/>
  <circle cx="${r}" cy="${r}" r="${r-5}" fill="none" stroke="#c0392b" stroke-width="0.8"/>
  ${getTextNodes()}
</svg>`;
}

export default function WorkProof() {
  const { theme } = useTheme();
  const { requirePro, isPro } = usePlan();   // 課金：稼働証明書の出力はProのみ
  const navigate = useNavigate();
  const [projects, setProjects]       = useState([]);
  const [workDates, setWorkDates]     = useState([]);
  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [generating, setGenerating]   = useState(false);
  const [pdfError, setPdfError]       = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterClient, setFilterClient] = useState('すべて');
  const [clients, setClients]         = useState([]);
  const [useHanko, setUseHanko]       = useState(true); // 電子印を押印するか
  const [filterMonth, setFilterMonth] = useState('すべて'); // 月フィルター

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      // ── デモモード ──────────────────────────────────────────
      if (isDemoMode()) {
        setProjects(DEMO_PROJECTS);
        setWorkDates(DEMO_WORK_DATES);
        setProfile(DEMO_PROFILE);
        setClients(DEMO_CLIENTS);
        setLoading(false);
        return;
      }
      // ── 通常モード ──────────────────────────────────────────
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return;
      const [{ data: prj }, { data: wd }, { data: pf }, { data: cl }] = await Promise.all([
        supabase.from('projects').select('*, clients(id,name)').eq('user_id', uid).order('created_at', { ascending: false }),
        supabase.from('project_work_dates').select('*').eq('user_id', uid).order('date'),
        supabase.from('profiles').select('name, signature, hanko').eq('id', uid).single(),
        supabase.from('clients').select('id, name').eq('user_id', uid).order('name'),
      ]);
      setProjects(prj || []);
      setWorkDates(wd || []);
      setProfile(pf || { name: '', signature: '', hanko: null });
      setClients(cl || []);
    } finally {
      setLoading(false);
    }
  }

  // 案件の「代表月」を取得（start_date or due_date or work_datesの最初の日付）
  function getProjectMonth(proj) {
    const ref = proj.start_date || proj.due_date;
    if (ref) return ref.slice(0, 7); // YYYY-MM
    const wd = workDates.filter(w => w.project_id === proj.id).sort((a,b) => a.date.localeCompare(b.date));
    if (wd.length > 0) return wd[0].date.slice(0, 7);
    return null;
  }

  // 月リストを生成（新しい順）
  const allMonths = [...new Set(
    projects.map(p => getProjectMonth(p)).filter(Boolean)
  )].sort((a, b) => b.localeCompare(a));

  const filteredProjects = projects.filter(p => {
    const clientOk = filterClient === 'すべて' || p.client_id === filterClient;
    const monthOk  = filterMonth  === 'すべて' || getProjectMonth(p) === filterMonth;
    return clientOk && monthOk;
  });

  const toggleSelect = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const selectAll = () => setSelectedIds(filteredProjects.map(p => p.id));
  const clearAll  = () => setSelectedIds([]);

  function buildMergedRows() {
    const sel = projects.filter(p => selectedIds.includes(p.id));
    const dailyRows  = []; // 日付単位（daily/hourly）
    const periodRows = []; // 期間単位（project/monthly）

    sel.forEach(proj => {
      const type = proj.work_type || 'project';
      const base = {
        projectTitle:  proj.title,
        clientName:    proj.clients?.name || '',
        paymentMethod: proj.payment_method || '銀行振込',
      };

      if (type === 'daily' || type === 'hourly') {
        // 稼働日テーブルから取得
        workDates.filter(w => w.project_id === proj.id).forEach(wd => {
          dailyRows.push({
            ...base,
            rowType:  'daily',
            date:     wd.date,
            dayName:  getDayName(wd.date),
            dayIndex: getDayIndex(wd.date),
            memo:     wd.memo || '',
          });
        });
      } else {
        // 納品・月契約 → 期間行として追加
        periodRows.push({
          ...base,
          rowType:    'period',
          start_date: proj.start_date || '',
          due_date:   proj.due_date   || '',
          memo:       proj.memo       || '',
        });
      }
    });

    dailyRows.sort((a, b) => a.date.localeCompare(b.date));
    // 期間行は後ろにまとめて表示
    return [...dailyRows, ...periodRows];
  }

  function buildClientLabel() {
    const sel = projects.filter(p => selectedIds.includes(p.id));
    return [...new Set(sel.map(p => p.clients?.name).filter(Boolean))].join('・');
  }

  // 電子印SVGを取得（プロフィールのhanko文字列からその場で生成）
  function getHankoSVG() {
    return buildHankoSVG(profile?.hanko || '', 56);
  }

  // ── PDF出力 ───────────────────────────────────────────────
  function exportPDF() {
    if (!requirePro('workProof')) return;   // 課金：Freeならアップグレードモーダル
    if (selectedIds.length === 0) { alert('出力する案件を選択してください'); return; }

    const rows         = buildMergedRows();
    const clientLabel  = buildClientLabel();
    const todayJP      = formatDateJP(new Date().toISOString().split('T')[0]);
    const issuerText   = (profile?.signature) ? String(profile.signature) : (profile?.name || '');
    const hankoSVG     = useHanko ? getHankoSVG() : null;
    const hankoB64     = hankoSVG
      ? `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(hankoSVG)))}`
      : null;

    const rowsHtml = rows.length === 0
      ? `<tr><td colspan="6" style="text-align:center;color:#9ca3af;padding:12px;">稼働日の記録がありません</td></tr>`
      : rows.map((row, idx) => {
          const bg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
          if (row.rowType === 'period') {
            // 納品・月契約：期間表示
            const period = row.start_date && row.due_date
              ? `${formatDateShort(row.start_date)} 〜 ${formatDateShort(row.due_date)}`
              : row.start_date ? `${formatDateShort(row.start_date)} 〜` : '期間未設定';
            return `<tr style="background:${bg};">
              <td colspan="2" style="color:#6b7280;font-size:8.5px;">${period}</td>
              <td>${row.projectTitle || ''}</td>
              <td>${row.clientName || ''}</td>
              <td>${row.paymentMethod || ''}</td>
              <td style="color:#6b7280;">${row.memo || ''}</td>
            </tr>`;
          }
          const dayColor = row.dayIndex === 0 ? '#dc2626' : row.dayIndex === 6 ? '#1d4ed8' : '#1e293b';
          return `<tr style="background:${bg};">
            <td>${formatDateShort(row.date)}</td>
            <td style="color:${dayColor};font-weight:600;">${row.dayName}</td>
            <td>${row.projectTitle || ''}</td>
            <td>${row.clientName || ''}</td>
            <td>${row.paymentMethod || ''}</td>
            <td style="color:#6b7280;">${row.memo || ''}</td>
          </tr>`;
        }).join('');

    const confirmStampHtml = hankoB64
      ? `<div class="stamp-label">確認印</div>
         <div class="stamp-img-wrap">
           <img src="${hankoB64}" class="hanko-img" alt="電子印">
         </div>`
      : `<div class="stamp-label">確認印</div>`;

    const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="UTF-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&family=Noto+Serif+JP:wght@700&display=swap" rel="stylesheet">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body {
    font-family: 'Noto Sans JP', 'Hiragino Kaku Gothic Pro', 'Meiryo', sans-serif;
    font-size: 10px;
    color: #1e293b;
    background: #fff;
    padding: 14mm 14mm 10mm;
  }
  h1 {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.4em;
    text-align: center;
    color: #1e293b;
    margin-bottom: 8px;
  }
  .divider { border: none; border-top: 1px solid #cbd5e1; margin: 6px 0; }
  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin: 8px 0 6px;
  }
  .client-name { font-size: 13px; font-weight: 700; color: #1e293b; }
  .issue-date  { font-size: 10px; color: #374151; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 9.5px;
  }
  thead tr { background: #1e293b; color: #fff; }
  thead th { padding: 5px 5px; text-align: left; font-weight: 600; letter-spacing: 0.02em; }
  tbody td { padding: 5px 5px; border-bottom: 1px solid #e2e8f0; }
  .total-row { background: #1e293b !important; color: #fff; font-weight: 700; }
  .total-row td { border: none; padding: 5px 5px; }
  .col-date    { width: 11%; }
  .col-day     { width: 5%; }
  .col-project { width: 25%; }
  .col-client  { width: 22%; }
  .col-payment { width: 16%; }
  .col-memo    { width: 21%; }
  .bottom-section { display: flex; gap: 12px; margin-top: 10px; align-items: flex-start; }
  .issuer-box {
    flex: 1;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 10px 12px;
    min-height: 66px;
  }
  .issuer-box .label { font-size: 8px; color: #94a3b8; font-weight: 600; letter-spacing: 0.05em; margin-bottom: 6px; }
  .issuer-box .line  { font-size: 9.5px; color: #1e293b; line-height: 1.9; }
  .stamps { display: flex; gap: 8px; }
  .stamp-box {
    width: 66px;
    height: 66px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 5px;
    position: relative;
  }
  .stamp-label { font-size: 8px; color: #94a3b8; }
  .stamp-img-wrap {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -42%);
    opacity: 0.88;
  }
  .hanko-img { width: 46px; height: 46px; }
  .footer {
    margin-top: 8px;
    border-top: 1px solid #e2e8f0;
    padding-top: 4px;
    text-align: right;
    font-size: 8.5px;
    color: #94a3b8;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { padding: 14mm 14mm 10mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <h1>稼 働 証 明 書</h1>
  <hr class="divider">
  <div class="header-row">
    <span class="client-name">${clientLabel ? clientLabel + '　御中' : ''}</span>
    <span class="issue-date">発行日：${todayJP}</span>
  </div>
  <hr class="divider">
  <table>
    <colgroup>
      <col class="col-date"><col class="col-day"><col class="col-project">
      <col class="col-client"><col class="col-payment"><col class="col-memo">
    </colgroup>
    <thead>
      <tr>
        <th>日付</th><th>曜</th><th>案件名</th>
        <th>クライアント</th><th>支払方法</th><th>備考</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
      <tr class="total-row">
        <td colspan="5">合計稼働日数</td>
        <td style="text-align:right;">${rows.filter(r => r.rowType === 'daily').length} 日</td>
      </tr>
    </tbody>
  </table>

  <div class="bottom-section">
    <div class="issuer-box">
      <div class="label">発行者</div>
      ${issuerText
        ? issuerText.split('\n').map(l => `<div class="line">${l}</div>`).join('')
        : '<div class="line" style="color:#9ca3af;">（プロフィールで署名・事業者情報を設定してください）</div>'
      }
    </div>
    <div class="stamps">
      <div class="stamp-box">${confirmStampHtml}</div>
      <div class="stamp-box"><div class="stamp-label">承認印</div></div>
    </div>
  </div>

  <div class="footer">${todayJP}　発行</div>

  <script>
    document.fonts.ready.then(() => {
      setTimeout(() => { window.print(); window.close(); }, 400);
    });
  </script>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=794,height=1123');
    if (!win) { alert('ポップアップがブロックされました。ブラウザのポップアップ許可設定を確認してください。'); return; }
    win.document.write(html);
    win.document.close();
  }

  // ── Excel出力（PDFと同フォーマット）──────────────────────────
  async function exportExcel() {
    if (!requirePro('workProof')) return;   // 課金：Freeならアップグレードモーダル
    if (selectedIds.length === 0) { alert('出力する案件を選択してください'); return; }
    setGenerating('excel');
    setPdfError('');
    try {
      if (!window.XLSX) {
        await new Promise((res, rej) => {
          const sc = document.createElement('script');
          sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
          sc.onload = res; sc.onerror = rej;
          document.head.appendChild(sc);
        });
      }
      const XLSX       = window.XLSX;
      const wb         = XLSX.utils.book_new();
      const today      = new Date().toISOString().split('T')[0];
      const todayJP    = formatDateJP(today);
      const rows       = buildMergedRows();
      const clientLbl  = buildClientLabel();
      // ── スタイル定義 ───────────────────────────────────────
      const DARK  = '1E293B'; // ヘッダー背景
      const RED   = 'DC2626'; // 日曜
      const BLUE  = '1D4ED8'; // 土曜
      const EVEN  = 'FFFFFF';
      const ODD   = 'F8FAFC';
      const BORDER = { style: 'thin', color: { rgb: 'E2E8F0' } };
      const BORDER_CELL = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };

      const titleStyle = {
        font: { bold: true, sz: 16, color: { rgb: DARK } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const headerStyle = {
        font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: DARK } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: BORDER_CELL,
      };
      const metaLabelStyle = {
        font: { bold: true, sz: 10, color: { rgb: '94A3B8' } },
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      const metaValueStyle = {
        font: { sz: 10, color: { rgb: DARK } },
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      const clientStyle = {
        font: { bold: true, sz: 13, color: { rgb: DARK } },
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      const dateStyle = {
        font: { sz: 10, color: { rgb: DARK } },
        alignment: { horizontal: 'right', vertical: 'center' },
      };
      const totalStyle = {
        font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: DARK } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: BORDER_CELL,
      };
      const totalNumStyle = {
        font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: DARK } },
        alignment: { horizontal: 'right', vertical: 'center' },
        border: BORDER_CELL,
      };

      const cellStyle = (isEven, dayIndex) => ({
        font: { sz: 10, color: { rgb: DARK } },
        fill: { fgColor: { rgb: isEven ? EVEN : ODD } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: BORDER_CELL,
      });
      const dayStyle = (isEven, dayIndex) => ({
        font: { sz: 10, bold: true, color: { rgb: dayIndex === 0 ? RED : dayIndex === 6 ? BLUE : DARK } },
        fill: { fgColor: { rgb: isEven ? EVEN : ODD } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: BORDER_CELL,
      });

      // ── データ構築 ─────────────────────────────────────────
      // 行番号管理
      // R1: タイトル（稼働証明書）
      // R2: 空
      // R3: 宛先 ／ 発行日
      // R4: 空
      // R5: テーブルヘッダー
      // R6〜: データ行
      // Rn+1: 合計行
      // Rn+2: 空
      // Rn+3〜: 発行者・確認印・承認印
      // Rn+last: フッター

      const DATA_START = 5; // 0-indexed: row index of table header
      const dataRows = rows.map((r, i) => {
        if (r.rowType === 'period') {
          const period = r.start_date && r.due_date
            ? `${formatDateShort(r.start_date)} 〜 ${formatDateShort(r.due_date)}`
            : r.start_date ? `${formatDateShort(r.start_date)} 〜` : '期間未設定';
          return [period, '', r.projectTitle, r.clientName, r.paymentMethod, r.memo];
        }
        return [formatDateShort(r.date), r.dayName, r.projectTitle, r.clientName, r.paymentMethod, r.memo];
      });

      const aoa = [
        ['稼 働 証 明 書', '', '', '', '', ''],          // 0: タイトル
        ['', '', '', '', '', ''],                          // 1: 空
        [clientLbl ? clientLbl + '　御中' : '', '', '', '', '発行日：', todayJP], // 2: 宛先/発行日
        ['', '', '', '', '', ''],                          // 3: 空
        ['日付', '曜', '案件名', 'クライアント', '支払方法', '備考'], // 4: ヘッダー
        ...dataRows,                                       // 5〜: データ
        ['合計稼働日数', '', '', '', '', `${rows.filter(r => r.rowType === 'daily').length} 日`],    // 合計行
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // ── セルスタイル適用 ───────────────────────────────────
      const R = (r, c) => XLSX.utils.encode_cell({ r, c });

      // タイトル行
      ws[R(0,0)].s = titleStyle;

      // 宛先・発行日行
      ws[R(2,0)].s = clientStyle;
      ws[R(2,4)].s = metaLabelStyle;
      ws[R(2,5)].s = metaValueStyle;

      // テーブルヘッダー
      ['日付','曜','案件名','クライアント','支払方法','備考'].forEach((_, c) => {
        if (ws[R(4,c)]) ws[R(4,c)].s = headerStyle;
      });

      // データ行
      rows.forEach((row, i) => {
        const r = DATA_START + 1 + i;
        const isEven = i % 2 === 0;
        [0,1,2,3,4,5].forEach(c => {
          if (!ws[R(r,c)]) ws[R(r,c)] = { v: '', t: 's' };
          ws[R(r,c)].s = c === 1 ? dayStyle(isEven, row.dayIndex) : cellStyle(isEven, row.dayIndex);
        });
      });

      // 合計行
      const totalR = DATA_START + 1 + rows.length;
      [0,1,2,3,4].forEach(c => {
        if (!ws[R(totalR,c)]) ws[R(totalR,c)] = { v: '', t: 's' };
        ws[R(totalR,c)].s = totalStyle;
      });
      if (ws[R(totalR,5)]) ws[R(totalR,5)].s = totalNumStyle;

      // ── 列幅・行高 ─────────────────────────────────────────
      ws['!cols'] = [
        { wch: 10 }, // 日付
        { wch: 5  }, // 曜
        { wch: 26 }, // 案件名
        { wch: 20 }, // クライアント
        { wch: 14 }, // 支払方法
        { wch: 30 }, // 備考
      ];

      // ── マージ ─────────────────────────────────────────────
      ws['!merges'] = [
        { s:{r:0,c:0}, e:{r:0,c:5} },   // タイトル
        { s:{r:2,c:0}, e:{r:2,c:3} },   // 宛先
        { s:{r:totalR,c:0}, e:{r:totalR,c:4} }, // 合計ラベル
      ];

      XLSX.utils.book_append_sheet(wb, ws, '稼働証明書');
      XLSX.writeFile(wb, `kadou_shomeisho_${today}.xlsx`);
    } catch(e) {
      setPdfError('Excel生成に失敗しました: ' + e.message);
      setTimeout(() => setPdfError(''), 5000);
    } finally {
      setGenerating(false);
    }
  }

  // ── UI ────────────────────────────────────────────────────
  const s = {
    card:  { background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:14, overflow:'hidden' },
  };

  const hankoSVGPreview = profile?.hanko ? getHankoSVG() : null;

  if (loading) return (
    <div style={{ padding:'20px 16px', textAlign:'center', color:theme.textMuted, paddingTop:60 }}>読み込み中...</div>
  );

  const mergedRows  = buildMergedRows();
  const clientLabel = buildClientLabel();

  return (
    <div style={{ padding:'20px 16px', maxWidth:680, margin:'0 auto' }}>

      {/* ヘッダー */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconWorkProof size={18}/> 稼働証明書</h1>
        <p style={{ fontSize:13, color:theme.textMuted, margin:'4px 0 0' }}>複数案件の稼働日を日付順にまとめて出力します</p>
      </div>

      {pdfError && (
        <div style={{ background:theme.dangerBg, border:'1px solid #7f1d1d', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:13, color:theme.dangerText }}>
          <IconWarning size={14}/> {pdfError}
        </div>
      )}

      {/* 発行者情報カード */}
      <div style={{ ...s.card, padding:'14px 18px', marginBottom:12 }}>
        <div style={{ fontSize:12, color:theme.textSecondary, marginBottom:8 }}>発行者（プロフィール設定から引継ぎ）</div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, color: profile?.signature ? theme.textPrimary : theme.textMuted, whiteSpace:'pre-line', lineHeight:1.7 }}>
              {profile?.signature || '（プロフィールの「署名・事業者情報」を設定してください）'}
            </div>
            {profile?.signature && (
              <span style={{ fontSize:11, background:theme.badgeBg5, color:theme.badgeTxt5, padding:'2px 8px', borderRadius:20, marginTop:6, display:'inline-block' }}><IconCheck size={14}/> 署名設定済み</span>
            )}
          </div>
          {/* 電子印プレビュー */}
          {hankoSVGPreview && (
            <div style={{ flexShrink:0, textAlign:'center' }}>
              <div style={{ fontSize:10, color:theme.textMuted, marginBottom:4 }}>電子印プレビュー</div>
              <div
                style={{ opacity:0.9 }}
                dangerouslySetInnerHTML={{ __html: hankoSVGPreview }}
              />
            </div>
          )}
        </div>
      </div>

      {/* 電子印オプション */}
      {hankoSVGPreview && (
        <div style={{ ...s.card, padding:'14px 18px', marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}><IconHanko size={14}/> 確認印に電子印を押す</div>
              <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>PDF出力時に確認印欄へ電子印を押印します（プロフィールで文字を設定）</div>
            </div>
            <button
              onClick={() => setUseHanko(v => !v)}
              style={{
                flexShrink: 0,
                width: 48, height: 26,
                borderRadius: 13,
                border: 'none',
                cursor: 'pointer',
                background: useHanko ? '#dc2626' : theme.badgeBg1,
                position: 'relative',
                transition: 'background 0.2s',
              }}
            >
              <span style={{
                position: 'absolute',
                top: 3, left: useHanko ? 24 : 4,
                width: 20, height: 20,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s',
                display: 'block',
              }} />
            </button>
          </div>
          {useHanko && (
            <div style={{ marginTop:10, padding:'8px 12px', background:theme.dangerBg2, borderRadius:8, fontSize:11, color:theme.dangerText, display:'flex', alignItems:'center', gap:6 }}>
                            <IconHanko size={12}/> 確認印欄に「{(profile?.hanko || '').slice(0,4) || '（未設定）'}」の電子印を押印します
            </div>
          )}
        </div>
      )}

      {/* 案件選択 */}
      <div style={{ ...s.card, padding:'16px 18px', marginBottom:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>出力する案件を選択</div>
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={selectAll} style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:8, padding:'5px 10px', fontSize:11, color:theme.textSecondary, cursor:'pointer' }}>全選択</button>
            <button onClick={clearAll}  style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:8, padding:'5px 10px', fontSize:11, color:theme.textSecondary, cursor:'pointer' }}>クリア</button>
          </div>
        </div>

        {/* 月フィルター */}
        {allMonths.length > 0 && (
          <div style={{ display:'flex', gap:6, marginBottom:10, overflowX:'auto', paddingBottom:4 }}>
            {['すべて', ...allMonths].map(m => {
              const label = m === 'すべて' ? 'すべて' : `${m.slice(0,4)}年${String(parseInt(m.slice(5,7)))}月`;
              const isActive = filterMonth === m;
              return (
                <button key={m} onClick={() => setFilterMonth(m)} style={{
                  flexShrink:0, padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:600, cursor:'pointer', border:'none',
                  background: isActive ? theme.accent : theme.bgInput,
                  color:      isActive ? '#fff'    : theme.textMuted,
                  outline:    isActive ? 'none'    : `1px solid ${theme.border}`,
                }}>{label}</button>
              );
            })}
          </div>
        )}

        {/* クライアントフィルター */}
        {clients.length > 0 && (
          <div style={{ display:'flex', gap:6, marginBottom:12, overflowX:'auto', paddingBottom:4 }}>
            {[{ id:'すべて', name:'すべて' }, ...clients].map(c => (
              <button key={c.id} onClick={() => setFilterClient(c.id)} style={{
                flexShrink:0, padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:500, cursor:'pointer', border:'none',
                background: filterClient === c.id ? theme.badgeBg8 : theme.bgInput,
                color:      filterClient === c.id ? theme.badgeTxt2 : theme.textMuted,
                outline:    filterClient === c.id ? 'none'    : `1px solid ${theme.border}`,
              }}><IconClients size={12}/> {c.name}</button>
            ))}
          </div>
        )}

        {filteredProjects.length === 0 ? (
          projects.length === 0 ? (
            <div style={{ textAlign:'center', padding:'28px 16px' }}>
              <p style={{ fontSize:13, color:theme.textSecondary, fontWeight:600, margin:'0 0 4px' }}>出力できる案件がありません</p>
              <p style={{ fontSize:12, color:theme.textMuted, margin:'0 0 14px', lineHeight:1.6 }}>
                稼働証明書は、案件の稼働日をまとめてPDF・Excelで出力する機能です。<br/>まず案件を登録しましょう。
              </p>
              <button onClick={() => navigate('/projects')}
                style={{ background:theme.primary, color:theme.primaryText, border:'none', borderRadius:10, padding:'10px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                案件を登録する
              </button>
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'24px 0', color:theme.textMuted, fontSize:13 }}>条件に合う案件がありません</div>
          )
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {filteredProjects.map(proj => {
              const isSelected = selectedIds.includes(proj.id);
              const cnt  = workDates.filter(w => w.project_id === proj.id).length;
              const type = proj.work_type || 'project';
              const typeLabel = type === 'daily' ? '単発' : type === 'hourly' ? '時給' : type === 'monthly' ? '月契約' : '納品';
              const typeColor = type === 'daily' ? theme.badgeTxt5 : type === 'hourly' ? theme.badgeTxt2 : type === 'monthly' ? theme.badgeTxt4 : theme.badgeTxt3;
              const typeBg    = type === 'daily' ? theme.badgeBg5 : type === 'hourly' ? theme.badgeBg2 : type === 'monthly' ? theme.badgeBg4 : theme.badgeBg3;
              const isPeriod  = type === 'project' || type === 'monthly';
              return (
                <div key={proj.id} onClick={() => toggleSelect(proj.id)} style={{
                  background: isSelected ? theme.accentBg : theme.bgInput,
                  border: `1px solid ${isSelected ? theme.accent : theme.border}`,
                  borderRadius:12, padding:'12px 16px', cursor:'pointer',
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600, color: isSelected ? theme.badgeTxt9 : theme.textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {proj.title}
                    </div>
                    <div style={{ display:'flex', gap:6, marginTop:4, flexWrap:'wrap' }}>
                      {proj.clients?.name && (
                        <span style={{ fontSize:11, background:theme.badgeBg7, color:theme.badgeTxt7, padding:'2px 8px', borderRadius:20 }}><IconClients size={12}/> {proj.clients.name}</span>
                      )}
                      <span style={{ fontSize:11, background:typeBg, color:typeColor, padding:'2px 8px', borderRadius:20 }}>
                        {typeLabel}
                      </span>
                      {isPeriod ? (
                        <span style={{ fontSize:11, background: isSelected ? theme.badgeBg4 : theme.bgCard, color: isSelected ? theme.badgeTxt4 : theme.textMuted, padding:'2px 8px', borderRadius:20 }}>
                          📅 {proj.start_date ? proj.start_date : '開始日未設定'} 〜 {proj.due_date ? proj.due_date : '終了日未設定'}
                        </span>
                      ) : (
                        <span style={{ fontSize:11, background: isSelected ? theme.badgeBg4 : theme.bgCard, color: isSelected ? theme.badgeTxt4 : theme.textMuted, padding:'2px 8px', borderRadius:20 }}>
                          📅 稼働 {cnt}日
                        </span>
                      )}
                      {proj.payment_method && (
                        <span style={{ fontSize:11, background:theme.bgCard, color:theme.textMuted, padding:'2px 8px', borderRadius:20 }}><IconPayment size={14}/> {proj.payment_method}</span>
                      )}
                    </div>
                  </div>
                  <span style={{ flexShrink:0, marginLeft:12, fontSize:20, color: isSelected ? theme.accent : theme.border }}>
                    {isSelected ? '✓' : '+'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {selectedIds.length > 0 && (
          <div style={{ marginTop:12, background:theme.bgInput, borderRadius:10, padding:'10px 14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:12, color:theme.textMuted }}>{selectedIds.length}件選択中</span>
              <span style={{ fontSize:13, fontWeight:700, color:theme.accent }}>合計 {mergedRows.length} 日</span>
            </div>
            {clientLabel && <div style={{ fontSize:11, color:theme.badgeTxt7 }}><IconClients size={12}/> {clientLabel}</div>}
            {mergedRows.length > 0 && (
              <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>
                {mergedRows[0].date} ～ {mergedRows[mergedRows.length - 1].date}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 出力ボタン */}
      <div style={{ display:'flex', gap:10 }}>
        <button onClick={exportPDF}
          disabled={selectedIds.length === 0}
          style={{
            flex:1, border:'none', borderRadius:12, padding:'14px', fontSize:14, fontWeight:600,
            cursor:     selectedIds.length === 0 ? 'default' : 'pointer',
            background: selectedIds.length > 0 ? theme.primary : theme.badgeBg1,
            color:      selectedIds.length > 0 ? '#fff'    : theme.textMuted,
          }}>
          <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <IconDownload size={15}/>PDF出力
            {!isPro && <span style={{ fontSize:9, fontWeight:700, letterSpacing:'0.05em', padding:'1px 5px', borderRadius:999, background:'rgba(255,255,255,0.2)' }}>PRO</span>}
          </span>
        </button>
        <button onClick={exportExcel}
          disabled={!!generating || selectedIds.length === 0}
          style={{
            flex:1, border:'none', borderRadius:12, padding:'14px', fontSize:14, fontWeight:600,
            cursor:     (generating || selectedIds.length === 0) ? 'default' : 'pointer',
            background: generating === 'excel' ? theme.badgeBg1 : selectedIds.length > 0 ? '#059669' : theme.badgeBg1,
            color:      generating === 'excel' ? theme.textMuted : selectedIds.length > 0 ? '#fff'    : theme.textMuted,
          }}>
          <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <IconDownload size={15}/>
            {generating === 'excel' ? '生成中...' : 'Excel出力'}
            {!isPro && <span style={{ fontSize:9, fontWeight:700, letterSpacing:'0.05em', padding:'1px 5px', borderRadius:999, background:'rgba(255,255,255,0.2)' }}>PRO</span>}
          </span>
        </button>
      </div>

      {selectedIds.length === 0 && projects.length > 0 && (
        <div style={{ marginTop:12, background:theme.badgeBg15, border:'1px solid #854d0e', borderRadius:12, padding:'12px 16px', fontSize:13, color:theme.colorYellow }}>
          <span style={{display:'flex',alignItems:'center',gap:6}}><IconWarning size={14} color={theme.colorYellow}/>出力する案件を選択してください</span>
        </div>
      )}
    </div>
  );
}
